import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * Hook customizado para gerenciar operações relacionadas aos serviços
 * @returns {Object} Funções e estados para gerenciar serviços
 */
const useServicos = () => {
  const [servicosDisponiveis, setServicosDisponiveis] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  // Carregar todos os serviços disponíveis
  const carregarServicos = useCallback(async () => {
    try {
      setCarregando(true);
      setErro(null);
      
      const response = await api.get('/servicos');
      if (response.data.success) {
        setServicosDisponiveis(response.data.data);
        return response.data.data;
      } else {
        console.error('Erro ao carregar serviços:', response.data.error);
        setErro(response.data.error || 'Erro ao carregar serviços');
        return [];
      }
    } catch (error) {
      console.error('Erro ao carregar serviços:', error);
      setErro(error.message || 'Erro ao carregar serviços do servidor');
      return [];
    } finally {
      setCarregando(false);
    }
  }, []);

  // Converter valor textual de prioridade para numérico (para salvar no banco)
  const prioridadeParaNumero = (prioridade) => {
    const mapeamento = {
      'baixa': 1,
      'normal': 2,
      'alta': 3,
      'urgente': 4
    };
    return mapeamento[prioridade] || 2; // Padrão: normal (2)
  };

  // Converter valor numérico de prioridade para textual (para exibir na interface)
  const numeroParaPrioridade = (numero) => {
    const mapeamento = {
      1: 'baixa',
      2: 'normal',
      3: 'alta',
      4: 'urgente'
    };
    return mapeamento[numero] || 'normal'; // Padrão: normal
  };

  // Converter serviços atendidos para formato do backend
  const formatarServicosAtendidosParaSalvar = (servicosAtendidos) => {
    const servicosAtendidosNumerico = {
      atende: servicosAtendidos.atende,
      prioridades: {}
    };
    
    // Converte cada prioridade de texto para número
    Object.keys(servicosAtendidos.prioridades).forEach(servicoId => {
      servicosAtendidosNumerico.prioridades[servicoId] = prioridadeParaNumero(
        servicosAtendidos.prioridades[servicoId]
      );
    });
    
    return servicosAtendidosNumerico;
  };

  // Converter serviços atendidos do formato do backend para formato da UI
  const formatarServicosAtendidosParaExibir = (servicosAtendidos) => {
    if (!servicosAtendidos) {
      return { prioridades: {}, atende: [] };
    }
    
    const prioridadesFormatadas = {};
    if (servicosAtendidos.prioridades) {
      Object.keys(servicosAtendidos.prioridades).forEach(servicoId => {
        prioridadesFormatadas[servicoId] = numeroParaPrioridade(servicosAtendidos.prioridades[servicoId]);
      });
    }
    
    return {
      prioridades: prioridadesFormatadas,
      atende: servicosAtendidos.atende || []
    };
  };

  // Filtrar serviços com base em um termo de busca
  const filtrarServicos = (termo) => {
    if (!termo) return servicosDisponiveis;
    
    return servicosDisponiveis.filter(
      servico => servico.nome.toLowerCase().includes(termo.toLowerCase()) || 
                servico.categoria?.toLowerCase().includes(termo.toLowerCase())
    );
  };

  // Carregar serviços ao montar o componente
  useEffect(() => {
    carregarServicos();
  }, [carregarServicos]);

  return {
    servicosDisponiveis,
    carregando,
    erro,
    carregarServicos,
    prioridadeParaNumero,
    numeroParaPrioridade,
    formatarServicosAtendidosParaSalvar,
    formatarServicosAtendidosParaExibir,
    filtrarServicos
  };
};

export default useServicos; 